import type { ITrackingError, ITrackerInstanceV2 } from '@zendesk-ui/tracking'

import config, { embeddedRuntime, hasZendeskProxy } from '@/config'
import captureException from '@/utils/error'

// True only when the app runs inside a Zendesk-proxied shell and outside the embedded
// extension iframe. The pigateway endpoint is only reachable from within the Zendesk
// shell, so gating on hasZendeskProxy avoids noisy 4xx in standalone deployments.
const isTrackingEnabled = () => hasZendeskProxy && !embeddedRuntime()

// debug mode: log to console instead of sending network requests outside production.
// Controlled by SENTRY_ENV so the same guard used by Sentry also gates tracking verbosity.
const isDebugMode = () => !['production', 'prod'].includes((config.sentryEnv ?? '').toLowerCase())

interface BaseTrackEventOptions {
  action?: 'click' | 'submit' | 'blur' | 'focus' | 'keydown' | 'play' | 'pause' | 'page_element_load'
  entityId?: string
  entityName?: string
  ticketId?: number
  brandId?: number
}

// customFields requires a registered custom schema and, per that schema, eventSubtype as its
// discriminator — model that pairing so a call site can't pass one without the other.
type TrackEventOptions = BaseTrackEventOptions &
  (
    | { eventSubtype?: string; customFields?: undefined }
    | { eventSubtype: string; customFields: Record<string, string | number | boolean> }
  )

// Tracker singleton — created once during initProductTracking(), null otherwise.
let tracker: ITrackerInstanceV2 | null = null
// Destroy handle returned by enableAutoTracking
let destroyAutoTracking: (() => void) | null = null
let initPromise: Promise<void> | null = null

/**
 * Initialise product-level tracking. Call once at app bootstrap after mount.
 * No-ops when:
 *   - running inside the embedded extension iframe
 *   - running in a non-Zendesk-proxied deployment
 */
export function initProductTracking(): Promise<void> {
  if (!isTrackingEnabled() || tracker) return Promise.resolve()
  // Guard against double-init (HMR, tests)
  if (initPromise) return initPromise

  initPromise = (async () => {
    const debug = isDebugMode()

    try {
      const { createTracker, enableAutoTracking, Capability } = await import('@zendesk-ui/tracking')
      const onError = (label: string) => (error: ITrackingError) => console.warn(`[tracking] ${label}`, error)

      tracker = createTracker({
        newTracker: true,
        autoTrack: true,
        debug,
        onError: onError('event delivery failed'),
      })

      destroyAutoTracking = enableAutoTracking({
        capability: Capability.Qa,
        trackLoad: true,
        trackRoutes: true,
        trackUnload: true,
        debug,
        onError: onError('page-view delivery failed'),
      })
    } catch (error) {
      destroyProductTracking()
      console.warn('[tracking] failed to initialize product tracking', error)
      captureException(error)
    }
  })()

  return initPromise
}

/**
 * Destroy the tracker and page-view listener. Useful for HMR cleanup.
 */
export function destroyProductTracking(): void {
  destroyAutoTracking?.()
  tracker?.destroy()
  destroyAutoTracking = null
  tracker = null
  initPromise = null
}

/**
 * Send an explicit tracking event. No-ops when tracker has not been initialised
 * (e.g. standalone / extension / test environments).
 *
 * @param eventName  Business action name, e.g. 'ai-insight-deleted'
 * @param subFeatureId  Numeric or string sub-feature identifier from `@/tracking/features/*`
 * @param options  Optional overrides (action defaults to 'click') plus standard schema fields
 */
export function trackEvent(eventName: string, subFeatureId: number | string, options: TrackEventOptions = {}): void {
  if (!tracker) return

  // Only include provided fields — the gateway rejects unexpected/undefined keys.
  const event: Parameters<typeof tracker.track>[0] = {
    eventName,
    subFeatureId: String(subFeatureId),
    action: options.action ?? 'click',
  }
  if (options.entityId !== undefined) event.entityId = options.entityId
  if (options.entityName !== undefined) event.entityName = options.entityName
  if (options.ticketId !== undefined) event.ticketId = String(options.ticketId)
  if (options.brandId !== undefined) event.brandId = String(options.brandId)
  if (options.eventSubtype !== undefined) event.eventSubtype = options.eventSubtype

  // Belt-and-suspenders for callers that bypass the type (e.g. plain JS, `as any`): the gateway
  // silently drops the whole event on this schema violation, so catch it here instead.
  if (options.customFields !== undefined) {
    if (options.eventSubtype === undefined) {
      console.warn(`[tracking] "${eventName}" passed customFields without eventSubtype — dropping customFields`)
    } else {
      event.customFields = options.customFields
    }
  }

  tracker.track(event)
}
